import { useEffect, useRef, useState } from "react";
import { API } from "@/context/AuthContext";
import { Sparkles, X, SendHorizonal } from "lucide-react";
import axios from "axios";

function sessionId() {
  let id = localStorage.getItem("nc_assistant_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("nc_assistant_session", id);
  }
  return id;
}

export default function Assistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [remaining, setRemaining] = useState(5);
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    const openIt = (e) => {
      setOpen(true);
      if (e.detail?.prefill) setInput(e.detail.prefill);
    };
    window.addEventListener("nc-open-assistant", openIt);
    return () => window.removeEventListener("nc-open-assistant", openIt);
  }, []);

  useEffect(() => {
    if (!open || loaded) return;
    axios.get(`${API}/assistant/session/${sessionId()}`).then((r) => {
      setMessages(r.data.messages.map((m) => ({ role: m.role, content: m.content })));
      setRemaining(r.data.limit - r.data.used);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [open, loaded]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming || remaining <= 0) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: text }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", content: err.detail || "Something broke. Try once more." };
          return c;
        });
        if (res.status === 429) setRemaining(0);
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + data.delta };
              return c;
            });
          }
          if (data.error) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: data.error };
              return c;
            });
          }
          if (data.done) setRemaining(data.remaining);
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Something broke on the way. Try once more." };
        return c;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[var(--ink)] text-[var(--paper)] px-4 py-3 text-sm font-semibold shadow-lg hover:-translate-y-0.5 transition-transform"
        data-testid="assistant-toggle-btn"
      >
        {open ? <X size={16} /> : <Sparkles size={16} />}
        {open ? "Close" : "Ask Novcovek"}
      </button>
      {open && (
        <div className="fixed bottom-20 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-96 rounded-3xl bg-[var(--paper)] border border-[var(--line)] assistant-panel overflow-hidden flex flex-col rise" style={{ maxHeight: "70vh" }} data-testid="assistant-panel">
          <div className="px-5 py-4 border-b border-[var(--line)] flex items-center justify-between">
            <div>
              <p className="font-display font-semibold text-lg">Novcovek</p>
              <p className="text-xs text-[var(--ink-soft)]">Points you to the right door.</p>
            </div>
            <span className="kicker text-[var(--ink-soft)]" data-testid="assistant-remaining">{remaining} left</span>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" style={{ minHeight: "180px" }}>
            {messages.length === 0 && (
              <div className="text-sm text-[var(--ink-soft)]">
                <p>Tell me what brought you here, in your own words. I will point you to the right of the seven places.</p>
                <p className="mt-2 text-xs">Five messages per visit, so make them count.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-sm rounded-2xl px-4 py-2.5 max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-[var(--ink)] text-[var(--paper)]" : "assistant-msg-a"}`}>
                {m.content || (
                  <span className="inline-flex gap-1">
                    <span className="tdot">●</span><span className="tdot">●</span><span className="tdot">●</span>
                  </span>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="px-4 py-3 border-t border-[var(--line)]">
            {remaining > 0 ? (
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="I keep waking at 3am and..."
                  className="flex-1 rounded-full border border-[var(--line)] bg-white/60 px-4 py-2.5 text-sm outline-none focus:border-[var(--ink)] transition-colors"
                  data-testid="assistant-input"
                />
                <button onClick={send} disabled={streaming || !input.trim()} className="btn-ink rounded-full p-2.5 disabled:opacity-40" data-testid="assistant-send-btn">
                  <SendHorizonal size={16} />
                </button>
              </div>
            ) : (
              <p className="text-xs text-[var(--ink-soft)] text-center py-1" data-testid="assistant-limit-note">
                That was five. The seven doors above know the rest.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
